import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, ExternalLink, Info } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const TestnetSetupPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🧪 Testnet Setup Guide</h1>
        <p className="text-gray-600 mb-8">Get started with FREE crypto testing on Polygon Amoy</p>

        {/* Step 1: Understanding Testnet */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
                1
              </div>
              <CardTitle>What is Testnet Testing?</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-700">
            <p>
              <strong>Testnet = Practice Mode</strong> for blockchain applications.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>FREE tokens</strong> - No real money needed</li>
              <li><strong>Same features</strong> - Everything works exactly like mainnet</li>
              <li><strong>No risk</strong> - Test as much as you want</li>
              <li><strong>Zero value</strong> - Tokens have no real-world value</li>
            </ul>
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Your Amoy testnet wallet is: <strong>The same address</strong> as your mainnet wallet!
                Just different network, different (free) tokens.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Step 2: Get Free Test Tokens */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
                2
              </div>
              <CardTitle>Get FREE Test Tokens</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              You need test MATIC (Mumbai's native token) to pay for gas fees and test transfers.
            </p>
            
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold text-gray-900 mb-2">Option 1: Polygon Faucet (Recommended)</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 mb-3">
                  <li>Go to Polygon Faucet</li>
                  <li>Connect your wallet OR paste your address</li>
                  <li>Select "Amoy" network</li>
                  <li>Click "Submit" to get free MATIC</li>
                  <li>Wait 1-2 minutes for tokens to arrive</li>
                </ol>
                <Button
                  onClick={() => window.open('https://faucet.polygon.technology/', '_blank')}
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Polygon Faucet
                </Button>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold text-gray-900 mb-2">Option 2: Alchemy Amoy Faucet</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Alternative faucet if Option 1 doesn't work
                </p>
                <Button
                  onClick={() => window.open('https://www.alchemy.com/faucets/polygon-amoy', '_blank')}
                  variant="outline"
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Alchemy Amoy Faucet
                </Button>
              </div>
            </div>

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Pro Tip:</strong> You'll get 0.1-0.5 test MATIC (enough for ~500 transactions!)
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Step 3: Test Your Wallet */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
                3
              </div>
              <CardTitle>Start Testing!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700">Once you have test MATIC:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-gray-700">
              <li>Create multiple test accounts on RouteFlow</li>
              <li>Set up usernames (@testuser1, @testuser2, etc.)</li>
              <li>Send test MATIC between accounts</li>
              <li>Try all features: QR codes, transaction history, analytics</li>
              <li>Verify transactions on <a href="https://amoy.polygonscan.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">Amoy Explorer</a></li>
            </ul>
            <Button
              onClick={() => navigate('/crypto/wallet')}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              size="lg"
            >
              Go to My Wallet →
            </Button>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle>Common Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-gray-700">
            <div>
              <strong>Q: Will my testnet wallet address be different?</strong>
              <p>A: No! Same address, just different network. Like using same phone number on WiFi vs cellular.</p>
            </div>
            <div>
              <strong>Q: Can I convert test tokens to real money?</strong>
              <p>A: No. Test tokens have ZERO value and cannot be sold or exchanged.</p>
            </div>
            <div>
              <strong>Q: How long does it take to get test tokens?</strong>
              <p>A: Usually 1-2 minutes after requesting from faucet.</p>
            </div>
            <div>
              <strong>Q: What if I run out of test tokens?</strong>
              <p>A: Just go back to the faucet and request more! Unlimited free refills.</p>
            </div>
            <div>
              <strong>Q: When should I switch to mainnet?</strong>
              <p>A: After you've tested everything thoroughly and are confident it all works perfectly.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestnetSetupPage;
